import { nowInTz } from 'src/helper/date';
import { SCHEDULING_BOOTSTRAP_TIMEZONE } from "src/redux/scheduling/constants";

// ===============================
// API Response Types
// ===============================

type RunOptimizerAffectedEntity = {
  index: number;
  identifier: string;
  label: string;
};

type RunOptimizerIssue = {
  attribute: string;
  category: string;
  message: string;
  entity?: {
    collection: string;
    label: string;
  };
};

type RunOptimizerErrorDetails = {
  category: string;
  known_pattern: string | null;
  summary: string;
  issue_count: number;
  affected_entities: {
    technicians?: RunOptimizerAffectedEntity[];
    work_orders?: RunOptimizerAffectedEntity[];
  };
  issues?: RunOptimizerIssue[];
};

export type RunOptimizerResponse = {
  status: "processing" | "failed";
  routing_request_uuid: string;
  error_code?: string;
  error_message?: string;
  error_details?: RunOptimizerErrorDetails;
};

export type RoutingStatusResponse = {
  status: "processing" | "succeeded" | "failed" | "none";
  routing_request_uuid: string;
  error_message?: string;
};

type ApplyStatusItem = {
  work_order_uuid: string;
  technician_uuid: string;
  status: "processing" | "queued" | "succeeded" | "skipped" | "failed";
  is_locked?: boolean;
  // Raw wire value, so snake_case here. Not a closed union: WorkOrderLock.lock_type has
  // no enum cast on the BE, so the persisted value is emitted unfiltered (ORI-569).
  lock_type?: string | null;
  reason_detail?: string;
};

export type ApplyStatusResponse = {
  is_active: boolean;
  routing_request_uuid: string;
  apply_batch_id: string;
  total: number;
  succeeded: number;
  skipped: number;
  failed: number;
  items: ApplyStatusItem[];
};

export type ApiError = {
  status?: number;
  message?: string;
  detail?: string;
  actionType?: "canceled";
};

// ===============================
// Shared Types
// ===============================

export type LockedSuggestion = {
  work_order_uuid: string;
  technician_uuid: string;
  scheduled_start_utc: string;
  scheduled_end_utc: string;
};

export type ScheduleFormValues = {
  selectedTechnicians: string[];
  selectedWOs: string[];
  rescheduleUnlocked: boolean;
};

export type WorkOrderPriority =
  | "normal"
  | "urgent"
  | "emergency"
  | "low_priority";

export type TimelineView = "day" | "week";

export type AvailabilityWindow = {
  date: string; // "YYYY-MM-DD"
  startTime: string; // "HH:MM"
  endTime: string; // "HH:MM"
};

export type LunchWindow = {
  date: string;
  startTime: string;
  endTime: string;
  lunchBreakDurationMinutes: number;
};

export type SchedulingHorizon = {
  startDate: string; // "YYYY-MM-DD"
  days: number;
};

export type TechnicianDailyStats = {
  totalWOs: number;
  totalWorkHours: number;
  totalTravelHours: number;
  travelTimeToBaseMinutes: number;
  utilizationPercentage: number;
  totalScheduledMinutes: number;
  totalPTOMinutes: number;
};

export type TechnicianSuggestedWO = {
  id: string;
  workOrderNumber: string;
  streetAddress: string;
  scheduledDate: string;
  startTime: string;
  endTime: string;
  locked: boolean;
  StartDateTimeUTC: string | null;
  endDateTimeUTC: string | null;
  applyStatus?: WorkOrderApplyStatus;
  isReschedule?: boolean;
  oldTechnicianName?: string | null;
  oldScheduledDate?: string | null;
  oldStartTime?: string | null;
  oldEndTime?: string | null;
};

export type TechnicianWithSuggestedWOs = {
  id: string;
  name: string;
  suggestedWOs: TechnicianSuggestedWO[];
};

export type OptimizerSummary = {
  total_work_orders: number;
  scheduled_work_orders: number;
  unscheduled_work_orders: number;
  scheduling_rate_percentage: number;
  total_technicians: number;
  average_utilization_percentage: number;
  total_days_scheduled: number;
  total_schedules: number;
};

// ===============================
// Locations
// ===============================

export type SchedulingLocation = {
  id: string;
  lat: number | null;
  lng: number | null;
  type?: string; // e.g. "home"
};

export type LocationsState = {
  byId: Record<string, SchedulingLocation>;
  allIds: string[];
};

// ===============================
// Work Orders
// ===============================

export type WorkOrderStatus =
  | "suggested"
  | "unassigned"
  | "unscheduled"
  | "assigned"
  | "schedule_in_progress"
  | "in_progress"
  | "completed"
  | "cancelled"
  | "awaiting";

export type WorkOrderApplyStatus =
  | "processing"
  | "succeeded"
  | "skipped"
  | "failed";

export type SchedulingWorkOrder = {
  id: string; // work_order_uuid
  workOrderNumber: string;
  streetAddress: string;
  locationId: string;
  priority: WorkOrderPriority;
  jobType: string | null;
  jobClassification: string | null;
  duration: number; // estimated_duration_minutes
  status: WorkOrderStatus;
  applyStatus?: WorkOrderApplyStatus;
  locked: boolean;
  // Origin of the server-side lock ('manual' | 'auto_day' | 'auto_urgent' |
  // 'auto_reschedule' in practice), null when the work order is not locked. Kept as
  // `string | null` rather than a union because the BE column has no enum cast and the
  // value is emitted unfiltered; consumers resolve it with a switch that has a default
  // branch. OPTIONAL so existing SchedulingWorkOrder object literals still compile.
  lockType?: string | null;
  residentAvailabilityWindows: AvailabilityWindow[];
  createdAt: string | null;
  serviceRequestUuid?: string;
  residentAvailabilityRequestPending?: boolean;
  // Populated when assigned:
  technicianId: string | null;
  scheduledDate: string | null;
  startDateTimeUTC: string | null;
  endDateTimeUTC: string | null;
  startTime: string | null;
  endTime: string | null;
  travelTimeMinutes: number | null;
  isReschedule?: boolean;
  unscheduledReasonDetail?: string;
  preOptimizerSchedule?: {
    technicianId: string | null;
    technicianName: string | null;
    scheduledDate: string | null;
    startTime: string | null;
    endTime: string | null;
    startDateTimeUTC: string | null;
    endDateTimeUTC: string | null;
  };
};

export type WorkOrdersState = {
  byId: Record<string, SchedulingWorkOrder>;
  allIds: string[];
  unscheduledWOIds?: string[];
};

// ===============================
// Technicians
// ===============================

export type SchedulingTechnician = {
  id: string;
  name: string | null;
  homeLocationId: string;
  hqLocationId?: string;
  availabilityWindows: AvailabilityWindow[];
  lunchWindows: LunchWindow[];
  lunchDurationMinutes: number;
  maxWorkHoursPerDay: number;
  status: string;
  displayColor: string | null;
  totalUtilization: number;
  workOrderIds: string[]; // from existing_daily_schedules.jobs
  scheduleBlockIds: string[]; // generated from lunchWindows
  suggestedWOIds: string[]; // from optimizer results ([] on initial load)
  dailyStats: Record<string, TechnicianDailyStats>; // keyed by "YYYY-MM-DD"
  originalDailyStats?: Record<string, TechnicianDailyStats>; // pre-optimizer snapshot, restored on reset
};

export type TechniciansState = {
  byId: Record<string, SchedulingTechnician>;
  allIds: string[];
};

// ===============================
// Schedule Blocks
// ===============================

export type ScheduleBlockType = "lunch" | "blocked";

export type ScheduleBlock = {
  id: string;
  technicianId: string;
  type: ScheduleBlockType;
  date: string; // "YYYY-MM-DD"
  startTime: string; // "HH:MM"
  endTime: string; // "HH:MM"
};

export type ScheduleBlocksState = {
  byId: Record<string, ScheduleBlock>;
  allIds: string[];
};

// ===============================
// Routes (map visualization)
// ===============================

export type MarkerType = "HQ" | "HOME" | "WORK_ORDER";

export type TechnicianLocation = {
  id: string;
  type: MarkerType;
  lng: number;
  lat: number;
  order?: number;
  /** True for optimizer-suggested stops — renders as light marker + dashed segment. */
  suggested?: boolean;
};

export type TechnicianRoute = {
  id: string;
  /** All stops for this day sorted by start time — scheduled and suggested interleaved. */
  stops: TechnicianLocation[];
};

export type TechnicianMapData = {
  technicianId: string;
  technicianName: string;
  /** Solid color — scheduled work-order markers and solid route segments. */
  color: string;
  /** Lighter variant — suggested work-order markers and dashed route segments. */
  suggestedColor: string;
  /** Primary base marker — home if the tech has one, otherwise HQ. Absent when neither location is valid. */
  hq?: TechnicianLocation;
  /** One route per day; stops are ordered by time across scheduled + suggested WOs. */
  routes: TechnicianRoute[];
};

export type MultiTechnicianData = TechnicianMapData[];

// ===============================
// Blockout Blocks (user-created)
// ===============================

export type BlockoutBlock = {
  id: string;
  technician_appointment_id?: string;
  technicianId: string;
  date: string; // "YYYY-MM-DD"
  startTime: string; // "HH:MM"
  endTime: string; // "HH:MM"
  title?: string;
};

export type BlockoutBlocksState = {
  byId: Record<string, BlockoutBlock>;
  allIds: string[];
};

// ===============================
// Emergency Reschedule Proposals
// ===============================

export type EmergencyRescheduleAffectedWO = {
  id: string;
  address: string;
  workOrderNumber: string;
  newTechnicianId: string;
  newTechnicianName: string;
  suggestedDate: string;
  suggestedStartTime: string;
  suggestedEndTime: string;
  suggestedStartUTC: string;
  suggestedEndUTC: string;
  travelTimeMinutes: number;
  oldTechnicianId: string | null;
  oldTechnicianName: string | null;
  oldScheduledDate: string | null;
  oldStartTime: string | null;
  oldEndTime: string | null;
  oldStartUTC: string | null;
  oldEndUTC: string | null;
};

export type EmergencyRescheduleProposal = {
  id: string;
  address: string;
  workOrderNumber: string;
  technicianId: string;
  technicianName: string;
  scheduledDate: string;
  startTime: string;
  endTime: string;
  startDateTimeUTC: string;
  endDateTimeUTC: string;
  travelTimeMinutes: number;
  affectedWorkOrders: EmergencyRescheduleAffectedWO[];
};

// ===============================
// Alert / Undo
// ===============================

export type SuggestedWORescheduleSnapshot = {
  scheduledDate: string | null;
  startTime: string | null;
  endTime: string | null;
  startDateTimeUTC: string | null;
  endDateTimeUTC: string | null;
  preOptimizerSchedule: NonNullable<
    SchedulingWorkOrder["preOptimizerSchedule"]
  >;
};

export type SchedulingUndoPayload =
  | { type: "blockoutUpdate"; original: BlockoutBlock; updated: BlockoutBlock }
  | { type: "blockoutCreate"; block: BlockoutBlock }
  | { type: "blockoutDelete"; block: BlockoutBlock }
  | {
      type: "suggestedWODelete";
      technicianId: string;
      workOrderId: string;
      rescheduleSnapshot?: SuggestedWORescheduleSnapshot;
    };

export type SchedulingAlertState = {
  message: string;
  undoPayload?: SchedulingUndoPayload;
} | null;

// ===============================
// Root Scheduling State
// ===============================

export type ApplyIneligibilityReason =
  | "organization_mismatch"
  | "state_not_completed"
  | "apply_batch_active"
  | "already_finalized"
  | "no_completed_request"
  | "superseded_by_newer_request"
  | "unknown";

export type SchedulingState = {
  selectedDate: string;
  timelineView: "day" | "week";
  timezone: string;
  isLoadingData: boolean;
  hasLoadError: boolean;
  isOptimizerRunning: boolean;
  optimizerHasRun: boolean;
  isMapVisible: boolean;
  isApplyingSuggestions: boolean;
  pendingAvailabilityRequestUuids: string[];
  routingRequestId: string | null;
  prevRoutingRequestId: string | null;
  applyBatchId: string | null;
  horizon: SchedulingHorizon | null;
  technicians: TechniciansState;
  workOrders: WorkOrdersState;
  locations: LocationsState;
  scheduleBlocks: ScheduleBlocksState;
  blockoutBlocks: BlockoutBlocksState;
  selectedTechnicianIds: string[];
  optimizerSummary: OptimizerSummary | null;
  pendingLockRollbacks: Record<string, boolean>;
  hoveredWorkOrderId: string | null;
  applyResult: {
    completed: boolean;
    total: number;
    succeeded: number;
    skipped: number;
    failed: number;
    assignedWOIds: string[];
  } | null;
  schedulingAlert: SchedulingAlertState;
  suggestedWOCount: number;
  emergencyRescheduleProposals: EmergencyRescheduleProposal[];
  lockedSuggestions: LockedSuggestion[];
};

const initialWorkOrders: WorkOrdersState = {
  byId: {},
  allIds: [],
  unscheduledWOIds: [],
};

const initialTechnicians: TechniciansState = {
  byId: {},
  allIds: [],
};

const initialLocations: LocationsState = {
  byId: {},
  allIds: [],
};

const initialScheduleBlocks: ScheduleBlocksState = {
  byId: {},
  allIds: [],
};

const initialBlockoutBlocks: BlockoutBlocksState = {
  byId: {},
  allIds: [],
};

export const schedulingInitialState: SchedulingState = {
  // The explicit argument is load-bearing: `nowInTz()` with no argument reads the
  // BROWSER's zone, which would seed a different calendar day than the zone on the
  // line below. Both fields are replaced by the first payload.
  selectedDate: nowInTz(SCHEDULING_BOOTSTRAP_TIMEZONE).format("YYYY-MM-DD"),
  timelineView: "day",
  timezone: SCHEDULING_BOOTSTRAP_TIMEZONE,
  isLoadingData: true,
  hasLoadError: false,
  isOptimizerRunning: false,
  optimizerHasRun: false,
  isMapVisible: true,
  isApplyingSuggestions: false,
  pendingAvailabilityRequestUuids: [],
  horizon: null,
  technicians: initialTechnicians,
  workOrders: initialWorkOrders,
  locations: initialLocations,
  scheduleBlocks: initialScheduleBlocks,
  blockoutBlocks: initialBlockoutBlocks,
  selectedTechnicianIds: [],
  optimizerSummary: null,
  pendingLockRollbacks: {},
  hoveredWorkOrderId: null,
  applyResult: null,
  applyBatchId: null,
  routingRequestId: null,
  prevRoutingRequestId: null,
  schedulingAlert: null,
  suggestedWOCount: 0,
  emergencyRescheduleProposals: [],
  lockedSuggestions: [],
};
